/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { getApiKey } from './api-keys'
import { parseGitHubUrl } from './github-service'

/**
 * Git operations for committing and pushing changes via GitHub API
 * Since this is a browser-based app, we use GitHub API instead of local git commands
 */

export interface GitStatus {
  modified: string[]
  added: string[]
  deleted: string[]
  untracked: string[]
  branch: string
  ahead: number
  behind: number
}

export interface CommitResult {
  success: boolean
  message: string
  commitHash?: string
}

export interface PushResult {
  success: boolean
  message: string
}

export interface FileChange {
  path: string
  content: string
  mode?: '100644' | '100755' | '040000' | '160000' | '120000' // file, executable, tree, commit, symlink
}

/**
 * Commit and push changes to GitHub repository
 * Uses GitHub API to create commits directly
 */
export async function commitAndPush(
  repoUrl: string,
  branch: string,
  commitMessage: string,
  files: FileChange[],
  onProgress?: (message: string) => void
): Promise<CommitResult> {
  try {
    const githubToken = getApiKey('github')
    if (!githubToken) {
      throw new Error('GitHub token not configured. Please add your GitHub token in Settings.')
    }

    const repoInfo = parseGitHubUrl(repoUrl)
    if (!repoInfo) {
      throw new Error('Invalid GitHub repository URL')
    }

    const { owner, repo } = repoInfo

    const headers: HeadersInit = {
      'Authorization': `Bearer ${githubToken}`,
      'Accept': 'application/vnd.github.v3+json',
      'Content-Type': 'application/json',
    }

    onProgress?.('Getting current branch reference...')

    // 1. Get the current commit SHA for the branch (if it exists)
    const refResponse = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/git/refs/heads/${branch}`,
      { headers }
    )

    let currentCommitSha: string | null = null
    let baseTreeSha: string | null = null

    if (refResponse.ok) {
      // Branch exists - get current commit
      const refData = await refResponse.json()
      currentCommitSha = refData.object.sha

      onProgress?.('Getting current tree...')

      // 2. Get the current commit to get the tree SHA
      const commitResponse = await fetch(
        `https://api.github.com/repos/${owner}/${repo}/git/commits/${currentCommitSha}`,
        { headers }
      )

      if (!commitResponse.ok) {
        throw new Error(`Failed to get commit: ${commitResponse.statusText}`)
      }

      const commitData = await commitResponse.json()
      baseTreeSha = commitData.tree.sha
    } else if (refResponse.status === 404) {
      // Branch doesn't exist yet - this is the initial commit
      console.log('[Git] Branch does not exist yet - creating initial commit')
      onProgress?.('Creating initial commit (new repository)...')
    } else {
      throw new Error(`Failed to get branch reference: ${refResponse.statusText}`)
    }

    onProgress?.('Creating blobs for changed files...')

    // 3. Create blobs for each file
    const tree: Array<{ path: string; mode: string; type: string; sha?: string; content?: string }> = []

    for (const file of files) {
      const blobResponse = await fetch(
        `https://api.github.com/repos/${owner}/${repo}/git/blobs`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            content: file.content,
            encoding: 'utf-8',
          }),
        }
      )

      if (!blobResponse.ok) {
        throw new Error(`Failed to create blob for ${file.path}: ${blobResponse.statusText}`)
      }

      const blobData = await blobResponse.json()

      tree.push({
        path: file.path,
        mode: file.mode || '100644',
        type: 'blob',
        sha: blobData.sha,
      })
    }

    onProgress?.('Creating new tree...')

    // 4. Create a new tree
    const treeBody: any = { tree }
    if (baseTreeSha) {
      // Only include base_tree if we have one (existing branch)
      treeBody.base_tree = baseTreeSha
    }

    const treeResponse = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/git/trees`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(treeBody),
      }
    )

    if (!treeResponse.ok) {
      throw new Error(`Failed to create tree: ${treeResponse.statusText}`)
    }

    const treeData = await treeResponse.json()

    onProgress?.('Creating commit...')

    // 5. Create a new commit
    const commitBody: any = {
      message: commitMessage,
      tree: treeData.sha,
    }
    if (currentCommitSha) {
      // Only include parents if we have a parent commit (existing branch)
      commitBody.parents = [currentCommitSha]
    }

    const newCommitResponse = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/git/commits`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(commitBody),
      }
    )

    if (!newCommitResponse.ok) {
      throw new Error(`Failed to create commit: ${newCommitResponse.statusText}`)
    }

    const newCommitData = await newCommitResponse.json()

    onProgress?.('Updating branch reference...')

    // 6. Update or create the branch reference to point to the new commit
    const refMethod = currentCommitSha ? 'PATCH' : 'POST'
    const refUrl = currentCommitSha
      ? `https://api.github.com/repos/${owner}/${repo}/git/refs/heads/${branch}`
      : `https://api.github.com/repos/${owner}/${repo}/git/refs`

    const refBody = currentCommitSha
      ? { sha: newCommitData.sha, force: false }
      : { ref: `refs/heads/${branch}`, sha: newCommitData.sha }

    const updateRefResponse = await fetch(refUrl, {
      method: refMethod,
      headers,
      body: JSON.stringify(refBody),
    })

    if (!updateRefResponse.ok) {
      throw new Error(`Failed to update branch: ${updateRefResponse.statusText}`)
    }

    onProgress?.('Commit pushed successfully!')

    // Log commit to .hivemind/commit-log.json
    try {
      await logCommitToHivemind({
        sha: newCommitData.sha,
        message: commitMessage,
        branch,
        repoUrl,
        files: files.map(f => f.path),
        timestamp: new Date().toISOString(),
      })
    } catch (logError) {
      console.warn('[Git] Failed to log commit to .hivemind:', logError)
      // Don't fail the commit if logging fails
    }

    return {
      success: true,
      message: `Successfully committed and pushed ${files.length} file(s) to ${branch}`,
      commitHash: newCommitData.sha.substring(0, 7),
    }
  } catch (error) {
    console.error('[Git] Commit and push failed:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Commit and push failed',
    }
  }
}

/**
 * Log a commit to .hivemind/commit-log.json
 */
async function logCommitToHivemind(commit: {
  sha: string
  message: string
  branch: string
  repoUrl: string
  files: string[]
  timestamp: string
}) {
  try {
    // This will be implemented when we have file system access in this context
    // For now, we'll just log it
    console.log('[Git] Commit logged:', commit)

    // TODO: Implement actual file writing when we have access to the file system
    // This will need to be called from App.tsx where we have access to the project store
  } catch (error) {
    console.error('[Git] Failed to log commit:', error)
  }
}

